/* eslint-disable no-process-env */
/**
 * Client-side runtime environment variable accessor
 *
 * This utility reads environment variables from the injected runtime config
 * (window.__RUNTIME_CONFIG__) rather than from build-time inlined values.
 *
 * Usage in client components:
 *
 * ```typescript
 * import { getRuntimeConfig } from '@lib/runtimeEnvClient';
 *
 * const backendUrl = getRuntimeConfig('NEXT_PUBLIC_BACKEND_URL');
 * ```
 */

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Record<string, string>;
  }
}
/**
 * ------------------------------------------------------------------
 * CONFIGURATION DEFINITION
 * ------------------------------------------------------------------
 * Define all allowed environment variables here.
 * This enables autocomplete and type checking in your IDE.
 */
export interface ClientRuntimeEnv {
  NEXT_PUBLIC_ENVIRONMENT: "local" | "development" | "staging" | "production";
  NEXT_PUBLIC_BACKEND_URL: string;
  NEXT_PUBLIC_AUTH_DOMAIN_URL: string;
  NEXT_PUBLIC_ANALYTICS_BASE_URL: string;
  NEXT_PUBLIC_GOOGLE_API_KEY: string;
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: string;
  NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID: string;
  NEXT_PUBLIC_SSO_ENABLED: "0" | "1";
  NEXT_PUBLIC_SEGMENT_ENABLED: "0" | "1";
  NEXT_PUBLIC_SEGMENT_KEY: string;
  NEXT_PUBLIC_INTERCOM_APP_ID: string;
  NEXT_PUBLIC_CLOUDINARY_API_KEY: string;
  NEXT_PUBLIC_CLOUDINARY_API_SECRET: string;
  NEXT_PUBLIC_DD_APPLICATION_ID: string;
  NEXT_PUBLIC_DD_CLIENT_TOKEN: string;
  NEXT_PUBLIC_MUX_ENV_KEY: string;
  NEXT_PUBLIC_BIRLA_WHITE_SECRET_KEY: string;
  NEXT_PUBLIC_FLAGSMITH_ENV_KEY: string;
  NEXT_PUBLIC_GEMINI_API_KEY: string;
  NEXT_PUBLIC_VERSION: string;
  NEXT_PUBLIC_FUZZY_MATCH_ACCURACY: number;
  ENCRYPTATION_SEED: string;
}

/**
 * Get a runtime configuration value from the injected config
 *
 * @param key - The configuration key (e.g., 'NEXT_PUBLIC_BACKEND_URL')
 * @param defaultValue - Optional default value if the config is not set
 * @returns The configuration value or defaultValue
 * @throws Error if the config is required but not set
 */
export function getRuntimeConfig<K extends keyof ClientRuntimeEnv>(
  key: K,
  defaultValue?: ClientRuntimeEnv[K]
): ClientRuntimeEnv[K] {
  if (typeof window === "undefined") {
    // Server-side: fallback to process.env for SSR compatibility
    const value = process.env[key] || defaultValue || "";

    return value as ClientRuntimeEnv[K];
  }

  const config = window.__RUNTIME_CONFIG__;

  if (!config) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(
      `Runtime config not initialized. Make sure window.__RUNTIME_CONFIG__ is set in the root layout.`
    );
  }

  const value = config[key];

  if (value === undefined || value === null) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`Required runtime config ${key} is not set`);
  }

  return value as ClientRuntimeEnv[K];
}

/**
 * Get a runtime configuration value, returning undefined if not set
 *
 * @param key - The configuration key
 * @returns The configuration value or undefined
 */
export function getRuntimeConfigOptional<K extends keyof ClientRuntimeEnv>(
  key: K
): ClientRuntimeEnv[K] | undefined {
  if (typeof window === "undefined") {
    return process.env[key] as ClientRuntimeEnv[K] | undefined;
  }

  const config = window.__RUNTIME_CONFIG__;

  return config?.[key] as ClientRuntimeEnv[K] | undefined;
}

/**
 * Check if runtime config is available
 *
 * @returns true if runtime config is initialized
 */
export function isRuntimeConfigAvailable(): boolean {
  if (typeof window === "undefined") {
    return true; // Server-side, fallback to process.env
  }

  const available = !!window.__RUNTIME_CONFIG__;

  return available;
}
